/*
  Click-and-drag horizontal scrolling for mouse users on any element with [data-drag-scroll].
  Touch devices keep native scrolling. While dragging (and while the release animation runs) the
  element gets `.is-dragging` so the section CSS can pause scroll-snap. On release the scroller
  keeps the momentum of the drag and eases onto the nearest snap point itself – so re-enabling
  scroll-snap afterwards never causes a visible jump.
*/
(() => {
  if (window.__evoDragScroll) return;
  window.__evoDragScroll = true;

  const canDrag = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // scroll positions at which direct children would snap (scroll-snap-align: start)
  const snapPoints = (el) => {
    const styles = getComputedStyle(el);
    const padding = parseFloat(styles.scrollPaddingLeft) || 0;
    const base = el.getBoundingClientRect().left;
    const points = [];
    for (const child of el.children) {
      if (getComputedStyle(child).scrollSnapAlign === 'none') continue;
      points.push(child.getBoundingClientRect().left - base + el.scrollLeft - padding);
    }
    return points;
  };

  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  const bind = (el) => {
    if (el.__dragScrollBound) return;
    el.__dragScrollBound = true;

    let pointerId = null;
    let startX = 0;
    let startScroll = 0;
    let moved = false;
    let samples = [];
    let animation = null;

    const cancelAnimation = () => {
      if (animation) cancelAnimationFrame(animation);
      animation = null;
    };

    const finish = () => {
      el.classList.remove('is-dragging');
      // let scroll listeners (loop wrap, dots) re-evaluate now that snapping is back on
      el.dispatchEvent(new Event('scroll'));
    };

    // momentum + eased landing on the nearest snap point
    const settle = (velocity) => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      const from = el.scrollLeft;
      // velocity is px/ms of the pointer; the content moves the other way
      let target = from - velocity * 220;
      target = Math.max(0, Math.min(maxScroll, target));

      const points = snapPoints(el).filter((p) => p >= -1 && p <= maxScroll + 1);
      if (points.length) {
        target = points.reduce((best, p) => (Math.abs(p - target) < Math.abs(best - target) ? p : best), points[0]);
      }

      const distance = target - from;
      if (Math.abs(distance) < 1 || reduceMotion.matches) {
        el.scrollLeft = target;
        finish();
        return;
      }

      const duration = Math.min(650, Math.max(300, Math.abs(distance) * 0.9));
      const startTime = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - startTime) / duration);
        el.scrollLeft = from + distance * easeOutCubic(t);
        if (t < 1) {
          animation = requestAnimationFrame(tick);
        } else {
          animation = null;
          finish();
        }
      };
      animation = requestAnimationFrame(tick);
    };

    el.addEventListener('pointerdown', (event) => {
      if (!canDrag.matches || event.pointerType !== 'mouse' || event.button !== 0) return;
      cancelAnimation();
      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = el.scrollLeft;
      moved = false;
      samples = [{ x: event.clientX, t: performance.now() }];
      el.classList.add('is-dragging');
    });

    el.addEventListener('pointermove', (event) => {
      if (pointerId === null || event.pointerId !== pointerId) return;
      const delta = event.clientX - startX;
      if (!moved && Math.abs(delta) > 4) {
        moved = true;
        el.setPointerCapture(pointerId);
      }
      if (moved) {
        event.preventDefault();
        el.scrollLeft = startScroll - delta;
        const now = performance.now();
        samples.push({ x: event.clientX, t: now });
        samples = samples.filter((s) => now - s.t <= 120);
      }
    });

    const stop = (event) => {
      if (pointerId === null || (event && event.pointerId !== undefined && event.pointerId !== pointerId)) return;
      if (moved && el.hasPointerCapture(pointerId)) el.releasePointerCapture(pointerId);
      pointerId = null;

      if (!moved) {
        el.classList.remove('is-dragging');
        return;
      }

      let velocity = 0;
      if (samples.length > 1) {
        const first = samples[0];
        const last = samples[samples.length - 1];
        const dt = last.t - first.t;
        if (dt > 0) velocity = (last.x - first.x) / dt;
      }
      settle(velocity);
    };

    el.addEventListener('pointerup', stop);
    el.addEventListener('pointercancel', stop);
    el.addEventListener('lostpointercapture', stop);

    // Swallow the click that ends a drag so buttons/links inside don't fire
    el.addEventListener(
      'click',
      (event) => {
        if (!moved) return;
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      },
      true
    );

    // a wheel/trackpad scroll while the release animation runs hands control back to the user
    el.addEventListener('wheel', () => {
      if (!animation) return;
      cancelAnimation();
      finish();
    }, { passive: true });
  };

  const init = (root = document) => root.querySelectorAll('[data-drag-scroll]').forEach(bind);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Theme editor re-renders sections
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
